import { useEffect, useId, useRef, useState, type ReactNode } from "react";

interface Props {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  /** When set, the confirm button stays disabled until this exact text is typed. */
  requireText?: string;
  busy?: boolean;
  onConfirm: (typed: string) => void;
  onCancel: () => void;
  extra?: ReactNode;
}

/** Native <dialog>: focus trap, Escape to close and inert background come from the platform. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = "primary",
  requireText,
  busy,
  onConfirm,
  onCancel,
  extra,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState("");
  const titleId = useId();
  const inputId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setTyped("");
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
    }
  }, [open]);

  const blocked = Boolean(requireText) && typed !== requireText;

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!blocked && !busy) onConfirm(typed);
        }}
      >
        <div className="modal-body">
          <h2 id={titleId}>{title}</h2>
          {children}
          {extra}
          {requireText && (
            <div className="field">
              <label htmlFor={inputId}>
                Type <code>{requireText}</code> to confirm
              </label>
              <input
                id={inputId}
                type="text"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className={`btn ${tone}`} disabled={blocked || busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
